/**
 * InteractiveTaskCard - Task card with real interactivity
 *
 * Features:
 * - Checkbox that calls complete_task API
 * - Due date countdown (overdue, due today, due in X)
 * - Priority color coding
 * - Click to expand/collapse details
 * - Double-click to open Kanban view
 *
 * Schema:
 * {
 *   type: 'interactive-task',
 *   task: { id, title, due_date, priority, status, account, description },
 *   showDetails: boolean,
 *   onComplete: { action: 'complete_task', task_id: 'uuid' }
 * }
 */

import React, { useState, useCallback } from 'react';
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Calendar,
  User,
  Flag,
  MoreHorizontal,
  Trash2,
  Edit3,
  CalendarPlus,
} from 'lucide-react';
import './InteractiveTaskCard.css';

// Priority configuration
const PRIORITY_CONFIG = {
  urgent: { color: 'var(--color-error)', label: 'Urgent', icon: AlertCircle },
  high: { color: 'var(--color-warning)', label: 'High', icon: Flag },
  medium: { color: 'var(--color-primary)', label: 'Medium', icon: Flag },
  low: { color: 'var(--color-text-muted)', label: 'Low', icon: Flag },
};

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'Pending', className: 'status-pending' },
  in_progress: { label: 'In Progress', className: 'status-progress' },
  completed: { label: 'Done', className: 'status-completed' },
  cancelled: { label: 'Cancelled', className: 'status-cancelled' },
};

/**
 * Calculate due date display
 */
function getDueDateDisplay(dueDate) {
  if (!dueDate) return null;

  const due = new Date(dueDate);
  const now = new Date();
  const diffMs = due - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMs < 0) {
    // Overdue
    const overdueDays = Math.abs(diffDays);
    if (overdueDays === 0) {
      const overdueHours = Math.abs(diffHours);
      return { text: `${overdueHours}h overdue`, status: 'overdue', urgent: true };
    }
    return { text: `${overdueDays}d overdue`, status: 'overdue', urgent: true };
  }

  if (diffDays === 0) {
    if (diffHours <= 2) {
      return { text: `Due in ${diffHours}h`, status: 'soon', urgent: true };
    }
    return { text: 'Due today', status: 'today', urgent: false };
  }

  if (diffDays === 1) {
    return { text: 'Due tomorrow', status: 'tomorrow', urgent: false };
  }

  if (diffDays <= 7) {
    return { text: `Due in ${diffDays} days`, status: 'upcoming', urgent: false };
  }

  // Format as date
  const formatted = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return { text: `Due ${formatted}`, status: 'future', urgent: false };
}

/**
 * InteractiveTaskCard Component
 */
export default function InteractiveTaskCard({
  task,
  showDetails: initialShowDetails = false,
  compact = false,
  onAction,
  onComplete,
  onEdit,
  onDelete,
  onSchedule,
  children,
}) {
  const [isChecked, setIsChecked] = useState(task?.status === 'completed');
  const [isExpanded, setIsExpanded] = useState(initialShowDetails);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  // Hooks must run unconditionally — the `!task` early return comes after them.

  // Handle checkbox click
  const handleCheckboxClick = useCallback(async (e) => {
    e.stopPropagation();
    if (isCompleting || isChecked) return;

    setIsCompleting(true);
    setIsChecked(true);

    try {
      if (onComplete) {
        await onComplete(task.id);
      } else if (onAction) {
        await onAction({
          type: 'complete_task',
          task_id: task.id,
          task_title: task.title,
          action: 'complete_task',
        });
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
      setIsChecked(false);
    } finally {
      setIsCompleting(false);
    }
  }, [task, isCompleting, isChecked, onComplete, onAction]);

  // Handle row click for expand/collapse
  const handleRowClick = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Handle menu actions
  const handleMenuAction = useCallback((action) => {
    setShowMenu(false);
    switch (action) {
      case 'edit':
        onEdit?.(task);
        onAction?.({ type: 'edit_task', task_id: task.id, task_title: task.title, action: 'edit_task' });
        break;
      case 'delete':
        onDelete?.(task.id);
        onAction?.({ type: 'delete_task', task_id: task.id, task_title: task.title, action: 'delete_task' });
        break;
      case 'schedule':
        onSchedule?.(task);
        onAction?.({ type: 'schedule_task', task_id: task.id, task_title: task.title, action: 'schedule_task' });
        break;
      default:
        break;
    }
  }, [task, onEdit, onDelete, onSchedule, onAction]);

  if (!task) return null;

  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const dueInfo = getDueDateDisplay(task.due_date);
  const PriorityIcon = priority.icon;

  return (
    <div
      className={`interactive-task ${isChecked ? 'interactive-task--completed' : ''} ${isExpanded ? 'interactive-task--expanded' : ''} ${compact ? 'interactive-task--compact' : ''}`}
      style={{ '--priority-color': priority.color }}
    >
      {/* Main row */}
      <div className="interactive-task__row" onClick={handleRowClick}>
        {/* Checkbox */}
        <button
          className={`interactive-task__checkbox ${isCompleting ? 'interactive-task__checkbox--loading' : ''}`}
          onClick={handleCheckboxClick}
          disabled={isCompleting || isChecked}
          aria-label={isChecked ? 'Task completed' : 'Mark task complete'}
        >
          {isChecked ? (
            <CheckCircle2 size={20} className="interactive-task__check-icon--done" />
          ) : (
            <Circle size={20} className="interactive-task__check-icon" />
          )}
        </button>

        {/* Content */}
        <div className="interactive-task__content">
          <div className="interactive-task__title-row">
            <span className={`interactive-task__title ${isChecked ? 'interactive-task__title--done' : ''}`}>
              {task.title}
            </span>
            {task.account && (
              <span className="interactive-task__account">
                <User size={12} />
                {task.account}
              </span>
            )}
          </div>

          <div className="interactive-task__meta">
            {/* Priority badge */}
            <span className="interactive-task__priority" style={{ color: priority.color }}>
              <PriorityIcon size={12} />
              {priority.label}
            </span>

            {/* Due date */}
            {dueInfo && (
              <span
                className={`interactive-task__due ${dueInfo.urgent ? 'interactive-task__due--urgent' : ''} interactive-task__due--${dueInfo.status}`}
              >
                <Clock size={12} />
                {dueInfo.text}
              </span>
            )}

            {/* Status if not pending */}
            {task.status && task.status !== 'pending' && (
              <span className={`interactive-task__status ${status.className}`}>
                {status.label}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="interactive-task__actions">
          {/* Expand toggle */}
          <button className="interactive-task__expand" aria-label="Toggle details">
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>

          {/* Menu */}
          <div className="interactive-task__menu-container">
            <button
              className="interactive-task__menu-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              aria-label="More options"
            >
              <MoreHorizontal size={18} />
            </button>

            {showMenu && (
              <div className="interactive-task__menu">
                <button onClick={() => handleMenuAction('edit')}>
                  <Edit3 size={14} /> Edit
                </button>
                <button onClick={() => handleMenuAction('schedule')}>
                  <CalendarPlus size={14} /> Schedule
                </button>
                <button onClick={() => handleMenuAction('delete')} className="interactive-task__menu-delete">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="interactive-task__details">
          {task.description && (
            <p className="interactive-task__description">{task.description}</p>
          )}

          <div className="interactive-task__detail-grid">
            {task.due_date && (
              <div className="interactive-task__detail-item">
                <Calendar size={14} />
                <span>Due: {new Date(task.due_date).toLocaleString()}</span>
              </div>
            )}
            {task.created_at && (
              <div className="interactive-task__detail-item">
                <Clock size={14} />
                <span>Created: {new Date(task.created_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="interactive-task__quick-actions">
            <button
              className="interactive-task__quick-action"
              onClick={() => handleMenuAction('schedule')}
            >
              <CalendarPlus size={14} />
              Add to Calendar
            </button>
            <button
              className="interactive-task__quick-action"
              onClick={() => handleMenuAction('edit')}
            >
              <Edit3 size={14} />
              Edit Task
            </button>
          </div>

          {children}
        </div>
      )}
    </div>
  );
}

/**
 * InteractiveTaskList - List of interactive tasks
 */
export function InteractiveTaskList({
  tasks = [],
  title,
  emptyMessage = 'No tasks',
  compact = false,
  showCompleted = false,
  onAction,
  groupBy, // 'priority' | 'status' | 'account' | null
}) {
  const filteredTasks = showCompleted
    ? tasks
    : tasks.filter((t) => t.status !== 'completed');

  if (filteredTasks.length === 0) {
    return (
      <div className="interactive-task-list interactive-task-list--empty">
        {emptyMessage}
      </div>
    );
  }

  // Group tasks if needed
  let groupedTasks = { all: filteredTasks };
  if (groupBy === 'priority') {
    groupedTasks = {
      urgent: filteredTasks.filter((t) => t.priority === 'urgent'),
      high: filteredTasks.filter((t) => t.priority === 'high'),
      medium: filteredTasks.filter((t) => t.priority === 'medium'),
      low: filteredTasks.filter((t) => t.priority === 'low'),
    };
  } else if (groupBy === 'status') {
    groupedTasks = {
      pending: filteredTasks.filter((t) => t.status === 'pending'),
      in_progress: filteredTasks.filter((t) => t.status === 'in_progress'),
      completed: filteredTasks.filter((t) => t.status === 'completed'),
    };
  }

  const renderGroup = (groupTasks, groupLabel) => {
    if (groupTasks.length === 0) return null;

    return (
      <div key={groupLabel} className="interactive-task-list__group">
        {groupLabel !== 'all' && (
          <div className="interactive-task-list__group-header">
            {groupLabel.charAt(0).toUpperCase() + groupLabel.slice(1).replace('_', ' ')}
            <span className="interactive-task-list__count">{groupTasks.length}</span>
          </div>
        )}
        {groupTasks.map((task) => (
          <InteractiveTaskCard
            key={task.id}
            task={task}
            compact={compact}
            onAction={onAction}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="interactive-task-list">
      {title && <h3 className="interactive-task-list__title">{title}</h3>}
      {Object.entries(groupedTasks).map(([group, groupTasks]) =>
        renderGroup(groupTasks, group)
      )}
    </div>
  );
}

/**
 * TaskStats - Summary stats for tasks
 */
export function TaskStats({ tasks = [], onAction }) {
  const overdue = tasks.filter((t) => {
    if (t.status === 'completed') return false;
    if (!t.due_date) return false;
    return new Date(t.due_date) < new Date();
  }).length;

  const dueToday = tasks.filter((t) => {
    if (t.status === 'completed') return false;
    if (!t.due_date) return false;
    const due = new Date(t.due_date);
    const today = new Date();
    return due.toDateString() === today.toDateString();
  }).length;

  const pending = tasks.filter((t) => t.status === 'pending' || t.status === 'in_progress').length;
  const completed = tasks.filter((t) => t.status === 'completed').length;

  const handleStatClick = (filter) => {
    onAction?.({ type: 'filter_tasks', filter });
  };

  return (
    <div className="task-stats">
      <button
        className={`task-stats__item ${overdue > 0 ? 'task-stats__item--alert' : ''}`}
        onClick={() => handleStatClick('overdue')}
      >
        <span className="task-stats__value">{overdue}</span>
        <span className="task-stats__label">Overdue</span>
      </button>
      <button
        className={`task-stats__item ${dueToday > 0 ? 'task-stats__item--warning' : ''}`}
        onClick={() => handleStatClick('due_today')}
      >
        <span className="task-stats__value">{dueToday}</span>
        <span className="task-stats__label">Due Today</span>
      </button>
      <button className="task-stats__item" onClick={() => handleStatClick('pending')}>
        <span className="task-stats__value">{pending}</span>
        <span className="task-stats__label">Pending</span>
      </button>
      <button className="task-stats__item task-stats__item--success" onClick={() => handleStatClick('completed')}>
        <span className="task-stats__value">{completed}</span>
        <span className="task-stats__label">Done</span>
      </button>
    </div>
  );
}
